import { motion, useInView } from "framer-motion";
import { useRef } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { ArrowRight, Package, Wheat, Briefcase } from "lucide-react";

const leatherItems = [
  {
    title: "Raw and Semi Processed Hides",
    desc: "Chrome-tanned wet blue hides from bovine animals — ready for further processing.",
    img: "https://customer-assets.emergentagent.com/job_global-trade-hub-105/artifacts/yhqvq0d2_pexels-oguz-kaan-boga-19166252-17315568.jpg",
    span: "span-2",
  },
  {
    title: "Finished Leather",
    desc: "Full-grain and corrected-grain finished leather for garments, upholstery, and accessories.",
    img: "https://customer-assets.emergentagent.com/job_global-trade-hub-105/artifacts/5bl0zgzi_pexels-edgar-martinez-2154660358-33425500.jpg",
    span: "",
  },
  {
    title: "Leather Wallets",
    desc: "Handcrafted wallets in bovine leather — bifold, trifold, and card holders.",
    img: "https://images.unsplash.com/photo-1677326768050-7d437347ca70?auto=format&fit=crop&w=600&q=80",
    span: "",
  },
  {
    title: "Leather Bags",
    desc: "Premium bags — messenger, tote, duffle, and laptop bags in genuine leather.",
    img: "https://customer-assets.emergentagent.com/job_global-trade-hub-105/artifacts/ibgj3pe6_pexels-dhanno-22434771.jpg",
    span: "",
  },
  {
    title: "Leather Footwear & More",
    desc: "Belts, key chains, passport covers, and custom leather goods on demand.",
    img: "https://customer-assets.emergentagent.com/job_global-trade-hub-105/artifacts/ai3gnrsb_pexels-adeelqureshiaq-29258015.jpg",
    span: "",
  },
];

const agroItems = [
  {
    title: "Fresh Fruits Export",
    desc: "Seasonal fruits including mangoes, bananas, pomegranates, and citrus — sourced from India's best farms.",
    img: "https://customer-assets.emergentagent.com/job_global-trade-hub-105/artifacts/bkivnylf_cover.jpg",
  },
  {
    title: "Vegetables Export",
    desc: "Fresh vegetables — onions, potatoes, green chillies, and specialty produce for global buyers.",
    img: "https://images.unsplash.com/photo-1768734837464-c5045c6a98c4?auto=format&fit=crop&w=600&q=80",
  },
  {
    title: "Fruits Import in India",
    desc: "We import premium dragon fruits and high-quality apples from select international orchards to serve growing domestic demand in India.",
    img: "https://customer-assets.emergentagent.com/job_global-trade-hub-105/artifacts/djbghd1t_pexels-any-lane-5945775.jpg",
  },
  {
    title: "Custom Agro Orders",
    desc: "We source and supply agro products on demand — tailored to your specific requirements.",
    img: "https://images.unsplash.com/photo-1706637839992-ccec411d4cbf?auto=format&fit=crop&w=600&q=80",
  },
];

function ServiceCard({ item, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      data-testid={`service-card-${index}`}
      className={`${
        item.span === "span-2" ? "span-2" : ""
      } img-zoom rounded-2xl overflow-hidden relative group cursor-pointer`}
    >
      <img
        src={item.img}
        alt={item.title}
        className="w-full h-full object-cover"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#1A1614]/80 via-[#1A1614]/20 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
        <h3
          className="text-white text-lg md:text-xl font-semibold mb-1"
          style={{ fontFamily: "'Playfair Display', serif" }}
        >
          {item.title}
        </h3>
        <p
          className="text-white/70 text-sm leading-relaxed opacity-0 group-hover:opacity-100 transition-opacity duration-300"
          style={{ fontFamily: "'Manrope', sans-serif" }}
        >
          {item.desc}
        </p>
      </div>
    </motion.div>
  );
}

export default function Services() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  const scrollTo = (href) => {
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="services"
      data-testid="services-section"
      className="py-24 md:py-32 bg-[#F4F0EC]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12" ref={ref}>
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-16 space-y-4"
        >
          <p className="font-accent text-[#4A6741] text-lg" data-testid="services-accent">
            What we offer
          </p>
          <h2
            data-testid="services-heading"
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1A1614] tracking-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Our Services
          </h2>
          <Separator className="w-16 h-0.5 bg-[#D4AF37] mx-auto" />
          <p
            className="max-w-xl mx-auto text-[#1A1614]/60 text-base"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            From premium leather in all forms to the freshest agro produce: we own every step, from quality control to final delivery.
          </p>
        </motion.div>

        {/* Tabs */}
        <Tabs defaultValue="leather" className="w-full" data-testid="services-tabs">
          <TabsList
            className="mx-auto w-fit bg-[#1A1614]/5 rounded-full p-1 mb-10"
            data-testid="services-tabs-list"
          >
            <TabsTrigger
              value="leather"
              data-testid="services-tab-leather"
              className="rounded-full px-6 py-2.5 text-sm font-medium data-[state=active]:bg-[#8D5A34] data-[state=active]:text-white data-[state=active]:shadow-md transition-all"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              <Package size={16} strokeWidth={1.5} className="mr-2" />
              Leather
            </TabsTrigger>
            <TabsTrigger
              value="agro"
              data-testid="services-tab-agro"
              className="rounded-full px-6 py-2.5 text-sm font-medium data-[state=active]:bg-[#4A6741] data-[state=active]:text-white data-[state=active]:shadow-md transition-all"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              <Wheat size={16} strokeWidth={1.5} className="mr-2" />
              Agro Products
            </TabsTrigger>
          </TabsList>

          <TabsContent value="leather" data-testid="services-content-leather">
            <div className="top-grid mb-3">
              {leatherItems.slice(0, 2).map((item, i) => (
                <ServiceCard key={i} item={{ ...item, span: "" }} index={i} />
              ))}
            </div>
            <div className="bento-grid">
              {leatherItems.slice(2).map((item, i) => (
                <ServiceCard key={i + 2} item={{ ...item, span: "" }} index={i + 2} />
              ))}
            </div>
            <div className="flex items-center gap-3 mt-6">
              <Briefcase size={16} strokeWidth={1.5} className="text-[#8D5A34]" />
              <p className="text-sm text-[#1A1614]/60" style={{ fontFamily: "'Manrope', sans-serif" }}>
                We deal in all kinds of <strong>Bovine Leather</strong> — available in all processing stages from raw to finished products.
              </p>
            </div>
          </TabsContent>

          <TabsContent value="agro" data-testid="services-content-agro">
            <div className="top-grid mb-3">
              {agroItems.slice(0, 2).map((item, i) => (
                <ServiceCard key={i} item={{ ...item, span: "" }} index={i} />
              ))}
            </div>
            <div className="top-grid">
              {agroItems.slice(2).map((item, i) => (
                <ServiceCard key={i + 2} item={{ ...item, span: "" }} index={i + 2} />
              ))}
            </div>
            <div className="flex items-center gap-3 mt-6">
              <Wheat size={16} strokeWidth={1.5} className="text-[#4A6741]" />
              <p className="text-sm text-[#1A1614]/60" style={{ fontFamily: "'Manrope', sans-serif" }}>
                We <strong>export</strong> fruits & vegetables and <strong>import</strong> select produce like dragon fruits and apples.
              </p>
            </div>
          </TabsContent>
        </Tabs>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <Button
            data-testid="services-cta"
            onClick={() => scrollTo("#inquiry")}
            className="rounded-full px-8 py-4 h-auto bg-[#8D5A34] text-white hover:bg-[#5D3A20] transition-all hover:scale-105 shadow-lg text-base"
          >
            Discuss Your Requirements
            <ArrowRight size={18} className="ml-2" />
          </Button>
        </motion.div>
      </div>
    </section>
  );
}
